import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import api from '../lib/api';
import './CSS/OperatorPanel.css';

export default function OperatorPanel({ onLogout }) {
  const [groupedNozzles, setGroupedNozzles] = useState({});
  const [selectedPump, setSelectedPump] = useState('');
  const [selectedNozzle, setSelectedNozzle] = useState('');
  const [amount, setAmount] = useState('');
  const [shifts, setShifts] = useState([]);

  const [lubeProducts, setLubeProducts] = useState([]);
  const [selectedLube, setSelectedLube] = useState('');
  const [lubeQty, setLubeQty] = useState('');
  const [lubePrice, setLubePrice] = useState(0);

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  const rawName = localStorage.getItem('name') || 'Operator';

  const cleanName = useMemo(() => {
    return String(rawName)
      .replace(/^["']+|["']+$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, [rawName]);

  useEffect(() => {
    fetchNozzles();
    fetchShifts();
    fetchLubes();
  }, []);

  useEffect(() => {
    // push one state so back button stays on this screen
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const fetchNozzles = async () => {
    try {
      const res = await api.get('/api/nozzles', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const grouped = {};
      res.data.forEach((n) => {
        if (!grouped[n.pump_name]) grouped[n.pump_name] = [];
        grouped[n.pump_name].push(n);
      });

      setGroupedNozzles(grouped);

      const firstPump = Object.keys(grouped)[0] || '';
      setSelectedPump(firstPump);
      setSelectedNozzle(grouped[firstPump]?.[0]?.id || '');
    } catch (err) {
      console.error('Failed to fetch nozzles', err);
    }
  };

  const fetchShifts = async () => {
    try {
      const res = await api.get(`/api/shifts/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch shifts', err);
    }
  };

  const fetchLubes = async () => {
    try {
      const res = await api.get('/api/lube-stock', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLubeProducts((res.data || []).filter((l) => l.is_active));
    } catch (err) {
      console.error('Failed to fetch lubes', err);
    }
  };

  const submitPayment = async (type) => {
    if (!selectedNozzle || isNaN(parseFloat(amount))) {
      return alert('Enter valid amount');
    }

    const payload = {
      operator_id: userId,
      nozzle_id: selectedNozzle,
      cash_collected: type === 'cash' ? parseFloat(amount) : 0,
      card_collected: type === 'card' ? parseFloat(amount) : 0,
      upi_collected: type === 'upi' ? parseFloat(amount) : 0,
    };

    try {
      await api.post('/api/shifts', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAmount('');
      fetchShifts();
    } catch (err) {
      console.error('Failed to submit payment', err);
      alert('Failed to record payment');
    }
  };

  const handleLubeSelect = (id) => {
    const prod = lubeProducts.find((l) => l.id === parseInt(id, 10));
    setSelectedLube(id);
    setLubePrice(prod ? prod.unit_price : 0);
  };

  const recordLubeSale = async () => {
    if (!selectedLube || !lubeQty || isNaN(lubeQty)) {
      return alert('Enter valid details');
    }

    const product = lubeProducts.find((p) => p.id === parseInt(selectedLube, 10));
    const total = (parseFloat(lubeQty) * parseFloat(lubePrice)).toFixed(2);

    const payload = {
      operator_id: userId,
      shift: 'current',
      product_id: selectedLube,
      quantity: lubeQty,
      total_amount: total,
      comment: ''
    };

    try {
      await api.post('/api/lube-sales', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ Sale Recorded: ${product?.product_name || 'Product'} - ₹${total}`);
      setLubeQty('');
      setSelectedLube('');
      setLubePrice(0);
      fetchLubes();
    } catch (err) {
      console.error('Failed to record lube sale', err);
      alert('Failed to record lube sale');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');

    if (typeof onLogout === 'function') {
      onLogout();
      return;
    }

    window.location.href = '/';
  };

  return (
    <div className="operator-container">
      <div className="operator-bg-shape shape-1"></div>
      <div className="operator-bg-shape shape-2"></div>
      <div className="operator-bg-shape shape-3"></div>

      <div className="operator-header">
        <div className="operator-header-left">
          <h2 className="operator-title">👷 Operator Panel</h2>

          <div className="operator-welcome">
            <span className="namaste-icon">🙏</span>
            <span className="namaste-text">NAMASTE {cleanName || 'Operator'}</span>
            <span className="namaste-icon">🙏</span>
          </div>
        </div>

        <button className="logout-btn-small" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <Tabs defaultActiveKey="fuel" className="operator-tabs mt-4">
        <Tab eventKey="fuel" title="⛽ Fuel Payments">
          <div className="operator-card mt-4 fade-up">
            <div className="section-title">Fuel Entry</div>

            <label className="form-label custom-label">Pump</label>
            <select
              className="form-select custom-input mb-3"
              value={selectedPump}
              onChange={(e) => {
                setSelectedPump(e.target.value);
                setSelectedNozzle(groupedNozzles[e.target.value]?.[0]?.id || '');
              }}
            >
              {Object.keys(groupedNozzles).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <label className="form-label custom-label">Nozzle</label>
            <select
              className="form-select custom-input mb-3"
              value={selectedNozzle}
              onChange={(e) => setSelectedNozzle(e.target.value)}
            >
              {groupedNozzles[selectedPump]?.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>

            <h5 className="amount-title">💰 Enter Payment Amount</h5>
            <input
              className="form-control form-control-lg custom-amount-input mb-4"
              type="number"
              inputMode="decimal"
              placeholder="₹ Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            <div className="payment-btn-wrap">
              <button className="action-btn cash-btn" onClick={() => submitPayment('cash')}>
                💵 Cash
              </button>
              <button className="action-btn card-btn" onClick={() => submitPayment('card')}>
                💳 Card
              </button>
              <button className="action-btn upi-btn" onClick={() => submitPayment('upi')}>
                📲 UPI
              </button>
            </div>
          </div>

          <div className="operator-card mt-4 fade-up delay-1">
            <div className="section-title">📋 Shift Summary</div>

            <ul className="summary-list">
              {shifts.length > 0 ? (
                shifts.map((s, i) => (
                  <li key={i} className="summary-item">
                    <div className="summary-top">
                      <strong>{s.nozzle_name || `Nozzle ${s.nozzle_id}`}</strong>
                      <span className="summary-amount">
                        ₹{(
                          Number(s.cash_collected || 0) +
                          Number(s.card_collected || 0) +
                          Number(s.upi_collected || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="summary-time">
                      🕒 {new Date(s.created_at).toLocaleString('en-IN')}
                    </div>
                  </li>
                ))
              ) : (
                <li className="summary-empty">No shift records yet.</li>
              )}
            </ul>
          </div>
        </Tab>

        <Tab eventKey="lube" title="🛢 Lube Sales">
          <div className="operator-card mt-4 fade-up">
            <div className="section-title">Lube Sales Entry</div>

            <label className="form-label custom-label">Select Lube Product</label>
            <select
              className="form-select custom-input mb-3"
              value={selectedLube}
              onChange={(e) => handleLubeSelect(e.target.value)}
            >
              <option value="">Select Product</option>
              {lubeProducts.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.product_name} ({l.brand})
                </option>
              ))}
            </select>

            {selectedLube && (
              <>
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label className="form-label custom-label">Available Stock</label>
                    <input
                      className="form-control custom-disabled"
                      disabled
                      value={
                        lubeProducts.find((l) => l.id === parseInt(selectedLube, 10))
                          ?.quantity_in_stock || 0
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label custom-label">Price / Unit</label>
                    <input
                      className="form-control custom-disabled"
                      disabled
                      value={lubePrice}
                    />
                  </div>
                </div>

                <label className="form-label custom-label">Quantity Sold</label>
                <input
                  className="form-control form-control-lg custom-amount-input mb-3"
                  type="number"
                  placeholder="Qty"
                  value={lubeQty}
                  onChange={(e) => setLubeQty(e.target.value)}
                />

                <h5 className="lube-total">
                  Total: ₹{(lubeQty && lubePrice) ? (lubeQty * lubePrice).toFixed(2) : '0.00'}
                </h5>

                <div className="text-center">
                  <button className="record-sale-btn" onClick={recordLubeSale}>
                    💾 Record Sale
                  </button>
                </div>
              </>
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}